import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Play } from 'lucide-react';
import FavoriteButton from './FavoriteButton';

const ContentCard = ({ item, type = 'movie', progress }) => {
  const id = item.stream_id || item.series_id || item.id;
  const title = item.name || item.title;
  const poster = item.stream_icon || item.cover || item.poster;
  const rating = item.rating || (item.rating_5based ? (item.rating_5based * 2).toFixed(1) : null);
  const year = item.year || item.releaseDate || item.added;

  return (
    <Link
      data-testid={`content-card-${type}-${id}`}
      to={`/${type}/${id}`}
      className={`group relative block rounded-lg overflow-hidden bg-gray-900 ${type === 'live' ? 'max-w-[200px]' : ''}`}
    >
      <div className={`${type === 'live' ? 'aspect-square' : 'aspect-[2/3]'} relative overflow-hidden`}>
        <img
          src={poster}
          alt={title}
          className={`w-full h-full transition-transform duration-500 group-hover:scale-110 ${type === 'live' ? 'object-contain p-4' : 'object-cover'}`}
          loading="lazy"
          onError={(e) => {
            e.target.src = type === 'live'
              ? 'https://via.placeholder.com/200x200?text=No+Logo'
              : 'https://via.placeholder.com/200x300?text=No+Poster';
          }}
        />
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Play Button on Hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center transform scale-75 group-hover:scale-100 transition-transform duration-300">
            <Play className="w-6 h-6 text-black fill-black ml-1" />
          </div>
        </div>

        {/* Rating Badge */}
        {rating && rating !== '0' && (
          <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-black/70 rounded-md">
            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
            <span className="text-xs text-white font-medium">{rating}</span>
          </div>
        )}

        {/* Favorite Button */}
        <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
          <FavoriteButton
            contentId={id}
            contentType={type}
            name={title}
            poster={poster}
            rating={rating}
            className="p-1.5 bg-black/60 rounded-full backdrop-blur-sm hover:bg-black/80"
          />
        </div>

        {/* Progress Bar */}
        {progress > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-700">
            <div className="h-full" style={{ width: `${Math.min(progress, 100)}%`, backgroundColor: 'var(--accent)' }} />
          </div>
        )}

        {/* Bottom Info on Hover */}
        <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <h3 className="text-white font-semibold text-sm line-clamp-2">{title}</h3>
          {year && (
            <span className="text-gray-400 text-xs mt-1 block">
              {typeof year === 'string' && year.length > 4 
                ? new Date(parseInt(year) * 1000).getFullYear() 
                : year}
            </span>
          )}
        </div>
      </div>

      {/* Title Below (always visible on mobile) */}
      <div className="p-2 sm:hidden">
        <h3 className="text-white text-sm font-medium line-clamp-1">{title}</h3>
        {rating && rating !== '0' && (
          <div className="flex items-center gap-1 mt-1">
            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
            <span className="text-xs text-gray-400">{rating}/10</span>
          </div>
        )}
      </div>
    </Link>
  );
};

export default ContentCard;
